import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { BillboardAd } from "../BillboardAd";
import { HERO_ADS, IMAGES } from "../../lib/data";
import { scrollToId } from "../../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const LedBillboard = () => {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setIdx((i) => (i + 1) % HERO_ADS.length), 4200);
        return () => clearInterval(t);
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.6, ease: EASE }}
            className="relative"
            data-testid="hero-billboard"
        >
            <div className="billboard-bezel led-glow !border-hotpink/40">
                <div className="billboard-screen">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={idx}
                            className="absolute inset-0"
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                        >
                            <BillboardAd {...HERO_ADS[idx]} />
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
            <div className="flex justify-center gap-16 sm:gap-24">
                <div className="h-12 sm:h-16 w-2 bg-gradient-to-b from-[#3f3f3f] to-transparent" />
                <div className="h-12 sm:h-16 w-2 bg-gradient-to-b from-[#3f3f3f] to-transparent" />
            </div>
            <div className="mx-auto -mt-3 h-8 w-[70%] rounded-full bg-hotpink/25 blur-2xl animate-glowpulse" />
        </motion.div>
    );
};

export const FunnelHero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yBill = useTransform(scrollYProgress, [0, 1], [0, 120]);

    return (
        <section id="top" ref={ref} className="relative min-h-screen overflow-hidden bg-noir pt-[68px]" data-testid="funnel-hero">
            <div className="absolute inset-0" aria-hidden="true">
                <img src={IMAGES.hero} alt="" className="h-full w-full object-cover opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-b from-noir/60 via-noir/80 to-noir" />
                <div className="absolute inset-0 led-dots opacity-60" />
                <div className="absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full bg-hotpink/15 blur-[130px]" />
            </div>
            <div className="noise-veil" />

            <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6 pt-12 pb-20 lg:pt-16 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center min-h-[calc(100vh-68px)]">
                <div>
                    <motion.p
                        initial={{ opacity: 0, letterSpacing: "0.5em" }}
                        animate={{ opacity: 1, letterSpacing: "0.3em" }}
                        transition={{ duration: 1, ease: EASE }}
                        className="font-alt text-[11px] sm:text-xs font-bold uppercase text-hotpink led-text-glow"
                        data-testid="hero-eyebrow"
                    >
                        WEALTHHAUS MEDIA
                    </motion.p>

                    <h1 className="mt-5 font-display font-extrabold text-[13vw] sm:text-8xl lg:text-[7rem] leading-[0.94] tracking-wide" data-testid="hero-headline">
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block text-white" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.15, ease: EASE }}>
                                GET YOUR
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block text-white" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.3, ease: EASE }}>
                                BUSINESS
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span className="block text-hotpink led-text-glow" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.45, ease: EASE }}>
                                SEEN.
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                        className="mt-5 max-w-xl text-sm sm:text-base leading-relaxed text-zinc-300"
                    >
                        Reach customers where they live, shop, work, travel, commute, and spend their time with
                        professional digital billboard advertising designed to increase visibility and put your
                        brand in front of more eyes.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1, ease: EASE }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <button className="btn-glossy-pink !px-8 !py-4 !text-sm" onClick={() => scrollToId("#quote")} data-testid="hero-quote-button">
                            GET MY BILLBOARD QUOTE <ArrowRight size={16} />
                        </button>
                        <button className="btn-chrome" onClick={() => scrollToId("#how")} data-testid="hero-how-button">
                            <Play size={15} /> SEE HOW IT WORKS
                        </button>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.2 }}
                        className="mt-7 font-alt text-[10px] font-semibold tracking-[0.26em] text-zinc-500 uppercase"
                    >
                        Put Your Brand in the Spotlight™
                    </motion.p>
                </div>

                <motion.div style={{ y: yBill }} className="relative px-2 sm:px-8">
                    <LedBillboard />
                </motion.div>
            </div>
        </section>
    );
};
